import React, { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import TagManager from 'react-gtm-module'
import { useHistory, useParams } from 'react-router-dom'
import {
  Button,
  leaderAssessmentsUrl,
  simple360,
  nameRegex,
  useUserService,
  useNotificationService,
  useErrorService,
} from '@abroad/components'
import LeaderManagerEvent from '../../constants/events'
import ReminderEvent from '../../constants/events'
import { leader360Type } from '../../utils/constants'
import { emailRegex } from '../../utils/utility'
import API from '../../utils/API'
import AddNewRequestInput from '../survey/AddNewRequestInput'

const RequestFooterButtons = ({
  type,
  addNewInputs,
  disabledParticularInput,
  canAddMembers,
  canViewResult,
  simpleDisable,
  setSimpleDisable,
  leaderDisable,
}) => {
  const { isValid, touched, values, handleSubmit } = useFormikContext()
  const source =
    type === leader360Type.SIMPLE360
      ? leader360Type.SIMPLE360
      : leader360Type.LEADER
  const maximumRequestsCreated = type === leader360Type.SIMPLE360 ? 20 : 15
  const [isDisabledAddNewInput, setIsDisabledAddNewInput] = useState(false)
  const [isDisabledRequestBtn, setIsDisabledRequestBtn] = useState(true)
  const [reminderLoading, setReminderLoading] = useState(false)
  const [isDisabledBtn, setIsDisabledBtn] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const Error = useErrorService()
  const Notification = useNotificationService()
  const { id } = useParams()
  const history = useHistory()
  const url = `/survey/requests/${leaderAssessmentsUrl}`
  const { user } = useUserService()
  const userId = user?.id
  const numberOfInputs = leader360Type.SIMPLE360 ? 1 : 3

  const getRequestedUsers = async () => {
    try {
      const { data } = await API.survey.getRequestedUsers()
      if (data) {
        setSimpleDisable(data.length === 0)
      }
    } catch (e) {
      Error.showError(e)
    }
  }

  useEffect(() => {
    if (values) setIsDisabledBtn(false)
  }, [values])

  useEffect(() => {
    if (
      maximumRequestsCreated <= values?.[source]?.length ||
      !canAddMembers ||
      canViewResult
    ) {
      setIsDisabledAddNewInput(true)
    } else {
      setIsDisabledAddNewInput(false)
    }
  }, [
    isValid,
    values,
    source,
    maximumRequestsCreated,
    canAddMembers,
    canViewResult,
  ])

  useEffect(() => {
    if (!isValid && values?.[source]?.length <= numberOfInputs) {
      setIsDisabledRequestBtn(true)
    } else {
      setIsDisabledRequestBtn(false)
    }
  }, [isValid, source, values, numberOfInputs])

  const filterUserRequestData = () => {
    const validUserData = values?.[source]?.filter(
      (user) =>
        user.firstName.match(nameRegex) &&
        user.email.match(emailRegex) &&
        !user.hasOwnProperty('_id'),
    )
    if (validUserData.length === 0) return []
    return validUserData
  }

  const disabledValidInputs = (data) => {
    for (let i = 0; i < data.length; i++) {
      const index = values?.[source]?.findIndex(
        (user) =>
          user.firstName === data[i].firstName && user.email === data[i].email,
      )
      disabledParticularInput(index, {
        firstName: data[i].firstName,
        email: data[i].email,
        _id: index,
        ...(type === leader360Type.LEADER && {
          hasRequestSent: true,
        }),
      })
    }
  }

  const foundDuplicateEmail = () => {
    const getDuplicateEmailRecord = values?.[source]?.reduce((a, e) => {
      a[e.email] = ++a[e.email] || 0
      return a
    }, {})

    const duplicateEmailObject = values?.[source]?.filter(
      (e) =>
        getDuplicateEmailRecord[e.email] &&
        e.email.match(emailRegex) &&
        e.firstName.match(nameRegex),
    )

    if (duplicateEmailObject.length > 0) {
      Notification.showNotification(
        'You seem to have provided same email more than once. Please provide unique email addresses.',
        'danger',
      )
      return true
    }
    return false
  }

  const updateUsersRequests = (data) => {
    if (type === leader360Type.SIMPLE360) {
      return API.survey.sendRequestedUsers({
        type: '360',
        surveyRequestData: data,
      })
    }
    if (id === 'new') {
      return API.survey.newLeaderRequests({ requestData: data })
    } else {
      return API.survey.updateLeaderRequests(id, { requestData: data })
    }
  }

  const gtmSendRequestAction = () => {
    if (type === leader360Type.SIMPLE360) {
      //GTM code
      TagManager.dataLayer({
        dataLayer: {
          event: LeaderManagerEvent.event.submitManagerRequest,
          eventProps: {
            category: LeaderManagerEvent.category.submitLARequestCategory,
            action: `User send ${simple360?.toLowerCase()} request`,
            label: `Send ${simple360?.toLowerCase()} request`,
            value: `User send ${simple360?.toLowerCase()} request.`,
            userId,
          },
        },
      })
    } else {
      //GTM code
      TagManager.dataLayer({
        dataLayer: {
          event: LeaderManagerEvent.event.submitLARequest,
          eventProps: {
            category: LeaderManagerEvent.category.submitManagerRequestCategory,
            action: 'User send leader assessment request',
            label: 'Send leader assessment request',
            value: `User send leader assessment request.`,
            userId,
          },
        },
      })
    }
  }

  const gtmReminderRequestAction = () => {
    if (type === leader360Type.SIMPLE360) {
      //GTM code
      TagManager.dataLayer({
        dataLayer: {
          event: ReminderEvent.event.submitReminderRequest,
          eventProps: {
            category: ReminderEvent.category.submitReminderRequestCategory,
            action: `User send  ${simple360?.toLowerCase()} reminder`,
            label: `Send ${simple360?.toLowerCase()} reminder`,
            value: `User send ${simple360?.toLowerCase()} reminder.`,
            userId,
          },
        },
      })
    } else {
      //GTM code
      TagManager.dataLayer({
        dataLayer: {
          event: ReminderEvent.event.submitLAReminderRequest,
          eventProps: {
            category: ReminderEvent.category.submitLAReminderRequestCategory,
            action: 'User send leader reminder request',
            label: 'Send leader reminder request',
            value: `User send leader reminder request.`,
            userId,
          },
        },
      })
    }
  }

  const checkNewFieldsAreEmpty = () => {
    const isMatch = values?.[source]?.find(
      (data) =>
        (data?.email !== '' || data?.firstName !== '') &&
        !data.hasOwnProperty('_id'),
    )
    return isMatch ? false : true
  }

  const chekcAnyFormInputIsInvalid = () => {
    const isMatch = values?.[source]?.find(
      (data) =>
        (data?.email === '' ||
          !data?.email.match(emailRegex) ||
          data?.firstName === '' ||
          !data?.firstName.match(nameRegex)) &&
        !(data?.email === '' && data?.firstName === ''),
    )
    return isMatch
  }

  const SendUserRequestshandle = async () => {
    if (type === leader360Type.LEADER && canViewResult) {
      // push user to view leader survey results
      history.push(`/survey/leader/${id}`)
      return
    }
    if (
      values?.[source]?.length > maximumRequestsCreated ||
      isDisabledRequestBtn
    )
      return

    const hasDuplicateEmail = foundDuplicateEmail()

    if (hasDuplicateEmail) return

    handleSubmit()

    const newFieldsAreEmpty = checkNewFieldsAreEmpty()

    if (newFieldsAreEmpty) return

    const isAnyInvalidInput = chekcAnyFormInputIsInvalid()

    if (isAnyInvalidInput) {
      Notification.showNotification(
        'Please provide valid name and email',
        'danger',
      )
      return
    }

    const surveyRequestData = filterUserRequestData()
    if (
      (Array.isArray(surveyRequestData) && surveyRequestData.length === 0) ||
      !surveyRequestData
    ) {
      Notification.showNotification(
        'Please provide valid name and email',
        'danger',
      )
      return
    }
    setIsLoading(true)

    const trimmedSurveyRequestData = surveyRequestData.map((request) => ({
      ...request,
      email: request?.email?.trim(),
      firstName: request?.firstName?.trim(),
    }))

    try {
      const { data } = await updateUsersRequests(trimmedSurveyRequestData)
      if (data) {
        disabledValidInputs(surveyRequestData)
        setIsDisabledBtn(true)
        setIsLoading(false)
        Notification.showNotification(
          `${
            type === leader360Type.SIMPLE360 ? simple360 : 'Leader'
          } requests have been sent`,
          'success',
        )
        gtmSendRequestAction()
        if (type === leader360Type.LEADER && id === 'new') {
          history.replace(`${url}/${data.id}`)
        }
        if (type === leader360Type.SIMPLE360) {
          getRequestedUsers()
        }
      }
    } catch (e) {
      Error.showError(e)
      setIsLoading(false)
    }
  }

  const handleSendReminder = async () => {
    setReminderLoading(true)
    try {
      if (type === leader360Type.SIMPLE360) {
        await API.survey.sendReminderForS360()
      } else {
        await API.survey.sendReminderForLeader(id)
      }
      Notification.showNotification(
        `${
          type === leader360Type.SIMPLE360 ? simple360 : 'Leader'
        } reminder have been sent`,
        'success',
      )
      gtmReminderRequestAction()
      setReminderLoading(false)
    } catch (error) {
      Error.showError(error)
      setReminderLoading(false)
    }
  }

  return (
    <div className='d-flex flex-column flex-sm-row justify-content-between mb-3 w-100 mt-4 pe-2'>
      <div>
        <AddNewRequestInput
          type={type}
          addNewInputs={addNewInputs}
          isDisabled={isDisabledAddNewInput}
        />
      </div>
      <div className='d-flex flex-column flex-sm-row justify-content-end w-100'>
        <Button
          variant='saffron'
          className=' border-radius-normal border-none mt-2 mt-sm-0 order-1 order-sm-0'
          disabled={simpleDisable || leaderDisable}
          onClick={handleSendReminder}
          isLoading={reminderLoading}>
          send reminder
        </Button>
        &nbsp;&nbsp;&nbsp;
        <Button
          variant='saffron'
          className='border-radius-normal border-none order-0 order-sm-1'
          disabled={
            canViewResult
              ? false
              : isDisabledRequestBtn ||
                isLoading ||
                Object.keys(touched).length === 0 ||
                !canAddMembers ||
                isDisabledBtn
          }
          onClick={SendUserRequestshandle}
          isLoading={isLoading}>
          {type === leader360Type.LEADER && canViewResult
            ? 'view results'
            : 'send requests'}
        </Button>
      </div>
    </div>
  )
}

export default RequestFooterButtons
